/* eslint-disable @typescript-eslint/no-unused-vars */
import { yupResolver } from '@hookform/resolvers/yup';
import { Badge, Box, Button, Grid, Step, StepLabel, Stepper } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import dayjs from 'dayjs';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { createEvent } from 'src/shared/services/eventService';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useMultiStepForm } from 'src/shared/hooks/useMultiStepForm';
import { MultiStepIcon } from 'src/shared/components/multistepper/multiStepIcon';
import { StyledMultiStepConnector } from 'src/shared/components/multistepper/styledMultiStepConnector';
import { ArrowBackIos, ArrowForwardIos, Check, PriorityHigh } from '@mui/icons-material';
import { useEventContext } from 'src/shared/contexts/eventContext';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { EventRequest } from '../models/eventRequest';
import { EventFormStepDescription } from './eventFormStepDescription';
import { EventFormStepDateAndPlace } from './eventFormStepDateAndPlace';
import { EventFormStepParticipants } from './eventFormStepParticipants';
import { EventFormStepContact } from './eventFormStepContact';
import { EventFormStepEventType } from './eventFormStepEventType';
import { useSchemaEvent } from '../schemas/useSchemaEvent';

type EventCreateModalProps = {
  modalOpen: boolean;
  onSave: () => void;
  onClose: () => void;
};

export const EventCreateModal = (props: EventCreateModalProps) => {
  const { modalOpen, onSave, onClose } = props;
  const { currentUser } = useGlobals();
  const { t } = useTranslation(['common', 'event']);
  const navigate = useNavigate();
  const isMounted = useIsMounted();

  const form = useRef(null);
  const [loading] = useState(false);
  const [serverErrors] = useState<string[]>([]);
  const { uploadedImageFile, selectedEventType } = useEventContext();

  const modalTop = useRef<HTMLDivElement>(null);
  const eventRequestPostSchema = useSchemaEvent();
  type EventRequestPostInterface = InferType<typeof eventRequestPostSchema>;

  const formMethods = useForm<EventRequestPostInterface>({
    resolver: yupResolver(eventRequestPostSchema),
    mode: 'all',
    defaultValues: {
      name: '',
      description: '',
      date: dayjs(Date.now()).toDate(),
      fee: undefined,
      memberFee: undefined,
      minParticipants: undefined,
      maxParticipants: undefined,
      address: undefined,
      postcode: undefined,
      city: undefined,
      lastDateForApplication: null,
      contactPersonName: '',
      contactPersonEmail: '',
      contactPersonPhone: '',
      allergy: true,
      publicSignUp: false,
      membersOnly: false,
      association: undefined,
      district: undefined,
      files: [],
      owner_id: 0,
      useTickets: false,
      tickets: [
        {
          ticketName: '',
          price: undefined,
          memberPrice: undefined,
          invoiceArticleId: undefined,
          createdById: currentUser?.user_id || 0,
          enabledAt: DateHelper.getDateTime(new Date()),
        },
      ],
    },
  });

  const { handleSubmit, watch } = formMethods;

  const onSubmit = useCallback(
    async (data: EventRequestPostInterface) => {
      const { useTickets, ...cleanData } = data;

      const dataToPost: EventRequest = {
        ...cleanData,
        date: DateHelper.postDateTime(data.date),
        lastDateForApplication: data.lastDateForApplication ? DateHelper.postDateTime(data.lastDateForApplication) : '',
        localAss_id: (data.association?.localAss_id !== undefined && data.association?.localAss_id) || 0,
        countyAss_id: (data.district?.countyAss_id !== undefined && data.district?.countyAss_id) || 0,
        files: uploadedImageFile ? [uploadedImageFile] : [],
      };

      ConsoleHelper.log('dataToPost', dataToPost);

      try {
        const res = await createEvent(dataToPost);
        ConsoleHelper.log(res);
        if (isMounted()) {
          toast.success(<>{t('event:saveSuccess')}</>);
          navigate(`${RoutesEnum.Events}/${res.data}`);
          onSave();
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error', e);
      }
    },
    [isMounted, navigate, onSave, t, uploadedImageFile],
  );

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const { step, steps, currentStepIndex, isFirstStep, isLastStep, back, next, goTo } = useMultiStepForm([
    <EventFormStepEventType />,
    <EventFormStepDescription />,
    <EventFormStepDateAndPlace />,
    <EventFormStepParticipants />,
    <EventFormStepContact />,
  ]);

  const useTickets = !!watch('useTickets');
  const ownerId = watch('owner_id');
  const fee = watch('fee');
  const memberFee = watch('memberFee');
  const eventTickets = watch('tickets') || [];

  const isEmpty = (v: unknown) => v === '' || v === null || v === undefined;

  const noParticipants =
    (isEmpty(watch('minParticipants')) && watch('minParticipants') !== 0) || (isEmpty(watch('maxParticipants')) && watch('maxParticipants') !== 0);
  const ownerMissing = !ownerId;
  const feeInvalidWhenFlat = !useTickets && (isEmpty(fee) || isEmpty(memberFee));
  const invalidTicketsWhenTicketMode =
    useTickets && (!eventTickets.length || eventTickets.some((ticket) => !ticket?.ticketName || ticket.ticketName.trim() === ''));

  const stepFourInvalid = noParticipants || ownerMissing || feeInvalidWhenFlat || invalidTicketsWhenTicketMode;
  const stepOneInvalid = false;
  const stepTwoInvalid = !watch('name');
  const stepThreeInvalid = !watch('date') || !watch('lastDateForApplication') || !watch('address') || !watch('postcode') || !watch('city');

  const isStepComplete = [!stepOneInvalid, !stepTwoInvalid, !stepThreeInvalid, !stepFourInvalid, true];

  const allStepsComplete = isStepComplete.every((v) => v === null || v);

  ConsoleHelper.log('eventcreate', watch());

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      disableBackdropClosing
      dialogTitle={t('event:createEvent')}
      dialogSubTitle={t(`event:eventType.${selectedEventType}`)}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogActions={
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {!isFirstStep ? (
            <Button type='button' variant='outlined' onClick={back} startIcon={<ArrowBackIos fontSize='small' />}>
              {t('common:button.back')}
            </Button>
          ) : (
            <Box />
          )}
          <Box sx={{ pr: 1 }}>
            {!isLastStep && (
              <Button type='button' variant='contained' sx={{ mr: 1 }} disableElevation onClick={next} endIcon={<ArrowForwardIos fontSize='small' />}>
                {t('common:button.continue')}
              </Button>
            )}
            <Badge
              sx={{
                '& .MuiBadge-badge': {
                  borderRadius: '100%',
                  height: '25px',
                },
                '& .badgeicon': {
                  fontSize: '0.8rem',
                },
              }}
              color={allStepsComplete ? 'success' : 'warning'}
              badgeContent={!allStepsComplete ? <PriorityHigh className='badgeicon' /> : <Check className='badgeicon' />}
            >
              <Button
                type='submit'
                disableElevation
                variant={isLastStep || allStepsComplete ? 'contained' : 'outlined'}
                disabled={!allStepsComplete}
                onClick={onHandleSave}
              >
                {t('common:button.saveClose')}
              </Button>
            </Badge>
          </Box>
        </Box>
      }
      dialogContent={
        <Box
          ref={modalTop}
          sx={{
            position: 'relative',
            border: (theme) => (serverErrors.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none'),
            minHeight: '22rem',
          }}
        >
          <LoadingOverlay loading={loading} />
          <Stepper activeStep={currentStepIndex} alternativeLabel sx={{ mb: 2, mt: 3 }} connector={<StyledMultiStepConnector />}>
            {steps.map((_item, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Step key={i} onClick={() => goTo(i)}>
                <StepLabel
                  StepIconComponent={(stepProps) => <MultiStepIcon {...stepProps} isDisabled={false} isComplete={isStepComplete[i] || false} />}
                >
                  {i + 1}. {t(`event:stepLabels.step${i}`)}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box sx={{ p: 2 }}>
            <GridWrapper>
              <Grid item xs={12} mt={1.5}>
                <FormProvider {...formMethods}>
                  <form onSubmit={handleSubmit(onSubmit)} ref={form}>
                    {step}
                  </form>
                </FormProvider>
              </Grid>
            </GridWrapper>
          </Box>
        </Box>
      }
    />
  );
};
